import { t, useLanguage } from "@/lib/i18n";
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TreePine, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import GuestTreeDialog from "@/components/GuestTreeDialog";
import GoogleSignIn from "@/components/GoogleSignIn";

export default function Login() {
  useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [guestOpen, setGuestOpen] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data);
      toast.success(t("Welcome back!"));
      const returnTo = location.state?.returnTo;
      navigate(returnTo?.startsWith("/verify-email") || returnTo?.startsWith("/change-password")
        ? returnTo : data.user.email_verified ? "/app" : "/verify-email");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#2C1E16] text-amber-50 p-12 relative overflow-hidden">
        <div className="parchment-grain absolute inset-0 opacity-40" />
        <Link to="/" className="relative flex items-center gap-2 font-serif text-2xl font-bold" data-testid="login-logo-link">
          {t(" VanshVriksham")}</Link>
        <div className="relative">
          <h2 className="font-serif text-4xl font-bold leading-tight">{t("Every name is a story worth keeping.")}</h2>
          <p className="mt-4 text-amber-100/70 max-w-md">{t("Secure your family's lineage in an encrypted vault built to outlive generations.")}</p>
          <div className="mt-8 flex items-center gap-2 text-sm text-[#D4A359]">
            <ShieldCheck size={18} />{t(" Bank-grade encryption & role-based access")}</div>
        </div>
        <div className="relative text-xs font-mono text-amber-100/40">{t("© 2026 VanshVriksham Heritage Vault")}</div>
      </div>

      <div className="flex items-center justify-center p-5 sm:p-8 bg-[#FAF7F2]">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-amber-800/80">{t("Welcome back")}</p>
            <h1 className="font-serif text-4xl font-bold mt-1">{t("Sign in")}</h1>
          </div>
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3" data-testid="login-error">
              {t(error)}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">{t("Email or mobile number")}</Label>
            <Input id="email" type="text" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" placeholder={t("Email or mobile number")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t("Password")}</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" placeholder="••••••••" />
          </div>
          <Link to="/reset-password" className="-mt-3 block text-right text-sm text-amber-800 hover:underline" data-testid="login-forgot-password-link">{t("Forgot password?")}</Link>
          <Button type="submit" disabled={loading} className="w-full gold-btn text-[#2C1E16] font-semibold border-0 h-11" data-testid="login-submit-button">
            {loading ? t("Signing in…") : t("Sign in")}
          </Button>
          <GoogleSignIn onSuccess={(data) => {
            login(data);
            const returnTo = location.state?.returnTo;
            navigate(returnTo?.startsWith("/verify-email") || returnTo?.startsWith("/change-password")
              ? returnTo : data.user.email_verified ? "/app" : "/verify-email");
          }} />
          <Button type="button" variant="outline" disabled={loading} onClick={() => setGuestOpen(true)} className="w-full h-11" data-testid="guest-login-button">{t("Continue as Guest")}</Button>
          <p className="text-xs text-stone-500 text-center">{t("Browse public member details with read-only access. No account needed.")}</p>
          <p className="text-sm text-stone-600 text-center">{t("No account?")}{" "}
            <Link to="/register" className="text-amber-800 font-semibold hover:underline" data-testid="login-to-register-link">{t("Create one")}</Link>
          </p>
        </form>
      </div>
      <GuestTreeDialog open={guestOpen} onClose={() => setGuestOpen(false)} />
    </div>
  );
}
